/**
 * 게시물 본문 표시 유틸 — 저장 형식과 화면 표현 사이의 변환.
 *
 * 배경
 *  - 서버는 저장 시 DOMPurify로 세정하며, 이 과정에서 본문의 `>`, `<`, `&`가
 *    `&gt;`, `&lt;`, `&amp;`로 직렬화된다. 서버 사이드 SEO 주입(index.ts injectMetaTags)이
 *    이 값을 HTML에 그대로 넣기 때문에 저장 형식은 이스케이프된 상태를 유지해야 한다.
 *  - 그래서 되돌리는 일은 화면 계층에서만 한다. 마크다운 렌더러는 raw HTML을 렌더링하지 않고
 *    (rehype-raw 미사용 + rehype-sanitize), 평문 경로는 React 텍스트 노드라 디코딩해도 안전하다.
 */

/** 저장 시 생긴 HTML 엔티티를 되돌린다. `&amp;`는 마지막에 풀어야 이중 디코딩이 생기지 않는다. */
export function decodeEntities(s: string): string {
  if (!s) return '';
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;|&#x27;|&apos;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&');
}

const MD_BLOCK_PATTERNS: RegExp[] = [
  /^#{1,6}\s+\S/m, // 제목
  /^\s*[-*+]\s+\S/m, // 글머리 목록
  /^\s*\d+\.\s+\S/m, // 번호 목록
  /^>\s?\S/m, // 인용
  /^\s*(?:---|\*\*\*|___)\s*$/m, // 구분선
  /^```/m, // 코드 블록
  /^\|.+\|\s*$/m, // 표
];
const MD_INLINE_PATTERNS: RegExp[] = [
  /\*\*[^*\n]+\*\*/, // 굵게
  /\[[^\]\n]+\]\((?:https?:\/\/|\/)[^)\s]+\)/, // 링크
];

/**
 * 마크다운으로 렌더링할 본문인지 판단한다.
 * 구 로직은 "**질문:**" 같은 AI 상담 패턴만 인식해, 증례 큐레이션·템플릿 증례가
 * 평문으로 찍히며 `**`, `>`가 그대로 노출됐다.
 */
export function looksLikeMarkdown(raw: string): boolean {
  const s = decodeEntities(raw);
  if (!s) return false;
  return MD_BLOCK_PATTERNS.some((re) => re.test(s)) || MD_INLINE_PATTERNS.some((re) => re.test(s));
}

/**
 * 피드 카드 미리보기용 평문 발췌. 굵게·인용·목록 기호를 걷어내고 링크는 텍스트만 남긴다.
 * 절 제목(### 환자 요약)과 빈 줄은 뺀다 — 3줄 클램프에서 제목과 빈 줄이 두 줄을 차지해
 * 실제 내용이 한 줄만 보였다. 게시물에는 별도 제목 필드가 있다.
 */
export function toPlainExcerpt(raw: string): string {
  const s = decodeEntities(raw);
  return s
    .split('\n')
    .filter((line) => !/^\s*#{1,6}\s/.test(line)) // 절 제목
    .filter((line) => !/^\s*(?:---|\*\*\*|___)\s*$/.test(line)) // 구분선
    .filter((line) => !/^\|?\s*:?-{3,}/.test(line)) // 표 구분 행
    .map((line) =>
      line
        .replace(/^#{1,6}\s+/, '')
        .replace(/^>\s?/, '')
        .replace(/^\s*[-*+]\s+/, '· ')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/__([^_]+)__/g, '$1')
        .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1$2')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/\[([^\]]+)\]\((?:[^)]+)\)/g, '$1')
        .replace(/^\|\s*|\s*\|\s*$/g, '')
        .replace(/\s*\|\s*/g, ' · ')
        .replace(/\s+$/g, '')
    )
    .filter((line) => line.trim() !== '') // 빈 줄 접기
    .join('\n')
    .trim();
}
